// frontend/src/App.tsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Link, useLocation } from 'react-router-dom';
import { 
  ThemeProvider, 
  CssBaseline, 
  AppBar, 
  Toolbar, 
  Typography, 
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Chip,
  Fade,
  Button,
  Avatar
} from '@mui/material';
import { 
  Folder as FolderIcon,
  SearchOutlined as SearchIcon,
  UploadFile as UploadFileIcon,
  InsertDriveFile as InsertDriveFileIcon,
  History as ActivityIcon,
  Menu as MenuIcon,
  HelpOutline as HelpOutlineIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Science as ScienceIcon,
  Archive as ArchiveIcon,
  BarChart as DashboardIcon,
  OndemandVideo as PayveIcon,
  VideoLibrary as VideoLibraryIcon,
  AssessmentOutlined as OpusAnalysisIcon,
} from '@mui/icons-material';
import FileList from './components/FileList';
import VideoPlayer from './components/VideoPlayer';
import CsvViewer from './components/CsvViewer'; 
import TextViewer from './components/TextViewer';
import ImageViewer from './components/ImageViewer';
import RecentFiles from './pages/RecentFiles';
import SearchFiles from './pages/SearchFiles';
import Home from './pages/Home';
import FolderBrowser from './pages/FolderBrowser';
import Activity from './pages/Activity';
import ErrorDashboard from './pages/ErrorDashboard';
import PayveFiles from './pages/PayveFiles';
import VideoAnalysis from './pages/VideoAnalysis';
import OpusAnalysisPage from './features/opus/pages/OpusAnalysisPage';
import theme from './theme';
import { fetchRecentFiles } from './services/api';
import './assets/fonts/fonts.css';
import './assets/styles/animations.css';
import './assets/styles/favicon.css';

// Define the logo path from the public folder
const logoPath = '/Roche_Logo_800px_Blue_RGB_Roche_Logo_RGB.png';

const drawerWidth = 240;
const rocheBlue = '#0B41CD';

const App: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [activePath, setActivePath] = useState('/');
  const [lastUploadTimestamp, setLastUploadTimestamp] = useState<string | null>(null);
  const location = useLocation(); // Moved inside Router via a wrapper component

  useEffect(() => {
    setPageLoaded(true);
    setActivePath(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    fetchRecentFiles(10, '.mp4')
      .then((response) => {
        if (response?.files?.length) {
          const sorted = [...response.files].sort((a, b) => {
            const ta = a.lastModified ? new Date(a.lastModified).getTime() : 0;
            const tb = b.lastModified ? new Date(b.lastModified).getTime() : 0;
            return tb - ta;
          });
          setLastUploadTimestamp(sorted[0].lastModified ?? null);
        }
      })
      .catch(() => {
        // Leave timestamp as null — UI will show fallback
      });
  }, []);

  const formatLastUpload = (iso: string | null): string => {
    if (!iso) return 'N/A';
    try {
      const date = new Date(iso);
      if (isNaN(date.getTime())) return 'N/A';
      // Produces e.g. "Jun 8, 2026 10:48 AM"
      const datePart = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      const timePart = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
      return `${datePart} ${timePart}`;
    } catch {
      return 'N/A';
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { text: 'Search', icon: <SearchIcon />, path: '/search', navTo: '/search', enabled: true, exact: false, external: false },
    { text: 'Activity', icon: <ActivityIcon />, path: '/activity', navTo: '/activity', enabled: true, exact: true, external: false },
    { text: 'Latest Uploads', icon: <UploadFileIcon />, path: '/recent-files', navTo: '/recent-files', enabled: true, exact: false, external: false },
    { text: 'Browse Files', icon: <InsertDriveFileIcon />, path: '/browse', navTo: '/browse', enabled: true, exact: false, external: false },
    { text: 'Browse Folders', icon: <FolderIcon />, path: '/folders', navTo: '/folders', enabled: true, exact: true, external: false },
    { text: 'PAYVE', icon: <PayveIcon />, path: '/payve', navTo: '/payve', enabled: true, exact: true, external: false },
    { text: 'Video Analysis', icon: <VideoLibraryIcon />, path: '/analysis', navTo: '/analysis', enabled: true, exact: true, external: false },
    { text: 'Opus Analysis', icon: <OpusAnalysisIcon />, path: '/opus-analysis', navTo: '/opus-analysis', enabled: true, exact: true, external: false },
    { text: 'R&D Storage', icon: <ScienceIcon />, path: '/folders/RD_Storage_Current', navTo: '/folders/RD_Storage_Current?foldersOnly=true', enabled: true, exact: false, external: false },
    { text: 'Historical R&D Storage', icon: <ArchiveIcon />, path: '/historical-rd-storage', navTo: 'https://onearchive-us.roche.com/my-collections/351/0', enabled: true, exact: false, external: true },
    { text: 'Error Dashboard', icon: <DashboardIcon />, path: '/error-dashboard', navTo: '/error-dashboard', enabled: true, exact: false, external: false },
  ];

  const isActive = (item: typeof navItems[0]) => {
    if (item.exact) return activePath === item.path;
    return activePath === item.path || activePath.startsWith(item.path + '/');
  };

  const drawer = (
    <div>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <Tooltip title={!item.enabled ? "Coming Soon" : ""} placement="right">
              <ListItemButton 
                component={item.enabled && !item.external ? Link : "div"}
                to={item.enabled && !item.external ? item.navTo : undefined}
                disabled={!item.enabled}
                onClick={() => {
                  if (!item.enabled) return;
                  if (item.external) {
                    window.open(item.navTo, '_blank', 'noopener,noreferrer');
                  } else {
                    setActivePath(item.path);
                  }
                }}
                sx={{ 
                  opacity: item.enabled ? 1 : 0.6,
                  '&.Mui-disabled': { color: 'text.disabled' },
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  backgroundColor: isActive(item) ? 'rgba(11, 65, 205, 0.08)' : 'transparent',
                  borderLeft: isActive(item) ? `4px solid ${rocheBlue}` : '4px solid transparent',
                  '&:hover': item.enabled ? {
                    backgroundColor: 'rgba(11, 65, 205, 0.05)',
                    '&::before': { opacity: 1, width: '4px' }
                  } : {},
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: isActive(item) ? '4px' : '0',
                    opacity: 0.7,
                    backgroundColor: rocheBlue,
                    transition: 'all 0.2s ease',
                  }
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    opacity: item.enabled ? 1 : 0.6,
                    color: isActive(item) ? 'primary.main' : 'inherit',
                    transition: 'color 0.2s ease'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: isActive(item) ? 500 : 400,
                          color: isActive(item) ? 'primary.main' : 'inherit',
                          transition: 'color 0.2s ease, font-weight 0.2s ease'
                        }}
                      >
                        {item.text}
                      </Typography>
                      {!item.enabled && (
                        <Chip 
                          label="Soon" 
                          size="small" 
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </div>
  );

  const container = window !== undefined ? () => window.document.body : undefined;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar 
        className="app-header"
        position="fixed" 
        color="default" 
        elevation={0}
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'white',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 3px 10px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <img src={logoPath} alt="Roche Logo" style={{ height: '42px', marginRight: '18px' }} />
              <Typography 
                component="div" 
                sx={{ 
                  color: rocheBlue,
                  fontFamily: "'Roche Sans', Arial, sans-serif",
                  fontWeight: 700,
                  fontSize: '2.2rem',
                  letterSpacing: '0.01em'
                }}
              >
                Open Bay Data Hub
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Tooltip title="Timestamp of the most recently uploaded video">
              <Box sx={{ display: 'flex', alignItems: 'center', color: rocheBlue }}>
                <AccessTimeIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                <Typography variant="body2" sx={{ color: rocheBlue, fontWeight: 500 }}>
                  Last update: {lastUploadTimestamp === null ? 'Loading…' : formatLastUpload(lastUploadTimestamp)}
                </Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Help documentation">
              <Button sx={{ borderRadius: 2, color: rocheBlue, fontWeight: 500 }} startIcon={<HelpOutlineIcon />}>
                Help
              </Button>
            </Tooltip>
            <Tooltip title="User profile: Coming soon">
              <Avatar sx={{ bgcolor: rocheBlue, width: 36, height: 36 }}>
                <PersonIcon />
              </Avatar>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', pt: 8 }}>
        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        >
          <Drawer
            container={container}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: drawerWidth,
                marginTop: '64px',
                boxShadow: '2px 0 8px rgba(0, 0, 0, 0.03)',
              },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: drawerWidth,
                mt: '64px',
                borderRight: '1px solid rgba(0, 0, 0, 0.08)',
                backgroundColor: theme.palette.background.paper,
                boxShadow: '2px 0 8px rgba(0, 0, 0, 0.03)',
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        <Fade in={pageLoaded} timeout={1200} style={{ transitionDelay: '200ms' }}>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: 3,
              width: { sm: `calc(100% - ${drawerWidth}px)` },
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Routes>
              {/* Main navigation routes */}
              <Route path="/" element={<Home />} />
              <Route path="/activity" element={<Activity />} />
              <Route path="/recent-files" element={<RecentFiles />} />
              <Route path="/browse" element={<FileList />} />
              <Route path="/folders/*" element={<FolderBrowser />} />
              <Route path="/payve" element={<PayveFiles />} />
              <Route path="/analysis" element={<VideoAnalysis />} />
              <Route path="/opus-analysis" element={<Navigate to="/opus-analysis/mapping" replace />} />
              <Route path="/opus-analysis/mapping" element={<OpusAnalysisPage mode="mapping" />} />
              <Route path="/opus-analysis/comparison" element={<OpusAnalysisPage mode="comparison" />} />
              <Route path="/error-dashboard" element={<ErrorDashboard />} />
              <Route path="/search" element={<SearchFiles />} />
              
              {/* File viewer routes */}
              <Route path="/play/:key" element={<VideoPlayer />} />
              
              {/* New routes for different file type viewers */}
              <Route path="/view/csv/:key" element={<CsvViewer />} />
              <Route path="/view/text/:key" element={<TextViewer />} />
              <Route path="/view/image/:key" element={<ImageViewer />} />
              
              {/* Fallback route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Box>
        </Fade>
      </Box>
    </ThemeProvider>
  );
};

// Wrapper to use hooks within Router
const AppWrapper: React.FC = () => (
  <Router>
    <App />
  </Router>
);

export default AppWrapper;
